// ChiPuoSfidare.tsx — v033: «Chi può sfidarti»: nessuno, chi ha il mio link, chiunque. Scegliendo «Chiunque» si apre
// una finestra che spiega cosa vuol dire (si compare in «Disponibili ora» e nella Wall of Fame quando l'app è aperta;
// ogni sfida arriva come richiesta e si decide ogni volta). Solo maggiorenni e con il nome utente (@nome).
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Finestra } from "../componenti/Finestra.tsx";
import { ErroreRete } from "../servizi/api.ts";
import type { Io } from "../servizi/account.ts";
import { impostaChiPuoSfidare, type ChiPuoSfidare } from "../servizi/amici.ts";
import { vibra } from "../servizi/nativo.ts";
import { tastiRadio } from "../componenti/tastiRadio.ts";

export function chiPuoSfidare(io: Pick<Io, "linkAttivo" | "apertoATutti">): ChiPuoSfidare {
  return io.apertoATutti ? "tutti" : io.linkAttivo ? "link" : "nessuno";
}

export function ScegliChiSfida({ io, soloChiunque = false, onCambiato }: { io: Io; soloChiunque?: boolean; onCambiato: () => void }) {
  const { t } = useTranslation();
  const [scelta, setScelta] = useState<ChiPuoSfidare>(chiPuoSfidare(io));
  const [spiega, setSpiega] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const [invio, setInvio] = useState(false);
  // v041: arrivando da «Sfida uno sconosciuto» (?evidenzia=chiunque) l'opzione «Chiunque» si illumina
  const [evidenzia] = useState(() => typeof window !== "undefined" && new URLSearchParams(window.location.search).get("evidenzia") === "chiunque");
  const rifTutti = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (evidenzia) rifTutti.current?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [evidenzia]);

  const salva = async (c: ChiPuoSfidare) => {
    const prima = scelta;
    setErrore(null);
    setInvio(true);
    setScelta(c);
    try {
      await impostaChiPuoSfidare(c);
      vibra(c === "tutti" ? "successo" : "scelta");
      onCambiato();
    } catch (e) {
      setScelta(prima);
      setErrore(e instanceof ErroreRete ? e.codice : "generico");
    } finally {
      setInvio(false);
    }
  };
  const scegli = (c: ChiPuoSfidare) => {
    if (c === scelta) return;
    if (c === "tutti") setSpiega(true);
    else void salva(c);
  };

  return (
    <div className="ld-chi-sfida">
      {soloChiunque ? (
        <button type="button" className="pulsante pulsante-primario" disabled={invio} onClick={() => setSpiega(true)}>
          {t("disponibili.accetta_chiunque")}
        </button>
      ) : (
        <div className="ld-chi-sfida-scelte" role="radiogroup" aria-label={t("amici.chi_sfida")} onKeyDown={tastiRadio}>
          {(["nessuno", "link", "tutti"] as const).map((c) => (
            <button
              key={c}
              type="button"
              role="radio"
              aria-checked={scelta === c}
              tabIndex={scelta === c ? 0 : -1}
              className="ld-chi-sfida-voce"
              ref={c === "tutti" ? rifTutti : undefined}
              data-evidenzia={c === "tutti" && evidenzia && scelta !== "tutti" ? true : undefined}
              disabled={invio}
              onClick={() => scegli(c)}
            >
              <span className="ld-chi-sfida-icona" aria-hidden="true">
                {c === "nessuno" ? "🔕" : c === "link" ? "🔗" : "🌍"}
              </span>
              <span className="ld-chi-sfida-testi">
                <strong>{t(`amici.chi_${c}`)}</strong>
                <span className="nota">{t(`amici.chi_${c}_nota`)}</span>
              </span>
            </button>
          ))}
        </div>
      )}
      {errore && (
        <p className="admin-errore" role="alert">
          {t(`social.errore_${errore}`, { defaultValue: t("account.errore_generico") })}
        </p>
      )}
      <Finestra aperta={spiega} titolo={t("amici.chiunque_titolo")} onChiudi={() => setSpiega(false)}>
        <ul className="ld-chiunque-punti">
          <li>{t("amici.chiunque_1")}</li>
          <li>{t("amici.chiunque_2")}</li>
          <li>{t("amici.chiunque_3")}</li>
          <li>{t("amici.chiunque_4")}</li>
        </ul>
        {!io.nomeUtente && <p className="nota">{t("social.errore_serve_nome_utente")}</p>}
        <div className="azioni-riga">
          <button type="button" className="pulsante pulsante-secondario" onClick={() => setSpiega(false)}>
            {t("generale.annulla")}
          </button>
          <button
            type="button"
            className="pulsante pulsante-primario"
            disabled={!io.nomeUtente || invio}
            onClick={() => {
              setSpiega(false);
              void salva("tutti");
            }}
          >
            {t("amici.chiunque_si")}
          </button>
        </div>
      </Finestra>
    </div>
  );
}
